/**
 * CharacterManager.js
 * Provides an interface to access the stats of all heroes and enemies
 */

import { Game, DesignDataTypes, CoreEvents } from "pixi_core";

export class CharacterManager {
    constructor() {
        /** @private @type {Object} */
        this._stats = {};

        Game.on(CoreEvents.eDESIGNDATAMANAGER_READY, this._onDesignDataReady, this);
    }

    /**
     * @private
     */
    _onDesignDataReady() {
        const heroesData = Game.design.getData("Heroes", DesignDataTypes.eGLOBAL_ENTITY);
        const enemiesData = Game.design.getData("Enemies", DesignDataTypes.eGLOBAL_ENTITY);

        for (const heroData of heroesData.children) {
            let copy = JSON.parse(JSON.stringify(heroData));

            // Remove level editor entity properties not useful in the game
            delete copy.id;
            delete copy.type;
            delete copy.x;
            delete copy.y;
            delete copy.children;
            delete copy.allowChildren;

            this._stats[heroData.name] = copy;
        }

        for (const enemyData of enemiesData.children) {
            let copy = JSON.parse(JSON.stringify(enemyData));

            // Remove level editor entity properties not useful in the game
            delete copy.id;
            delete copy.type;
            delete copy.x;
            delete copy.y;
            delete copy.children;
            delete copy.allowChildren;

            this._stats[enemyData.name] = copy;
        }
    }

    /**
     * @public
     * @param {String} characterId
     * @returns {Object}
     */
    getStats(characterId) {
        const stats = this._stats[characterId];
        if (stats) {
            return stats;
        } else {
            console.error("[CharacterManager.getState] Character id not found : " + characterId);
            return null;
        }
    }

    /**
     * @public
     * @param {String} characterId
     * @returns {Number}
     */
    getHP(characterId) {
        return Game.utils.assertNumber(this._getStatsProperty(characterId, "HP"), 0);
    }

    /**
     * @public
     * @param {String} characterId
     * @returns {Number}
     */
    getAbilityCooldown(characterId) {
        return Game.utils.assertNumber(this._getStatsProperty(characterId, "AbilityCooldown"), 0);
    }

    /**
     * @public
     * @param {String} characterId
     * @returns {Number}
     */
    getAttackDamage(characterId) {
        return Game.utils.assertNumber(this._getStatsProperty(characterId, "AttackDamage"), 0);
    }

    /**
     * @public
     * @param {String} characterId
     * @returns {Number}
     */
    getWeakness(characterId) {
        return Game.utils.assertValue(this._getStatsProperty(characterId, "Weakness"), []);
    }

    /**
     * @private
     * @param {String} characterId
     * @param {String} propertyName
     * @returns {Number}
     */
    _getStatsProperty(characterId, propertyName) {
        const stats = this._stats[characterId];
        if (stats) {
            return stats[propertyName];
        } else {
            console.error("[CharacterManager._getStatsProperty] Character id not found : " + characterId);
            return null;
        }
    }
}
